import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../../../core/basic/Modal";
import { Flex } from "../../../../../styled-system/jsx";
import { Button } from "../../../core/basic/Button";
import { css } from "../../../../../styled-system/css";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../../../zodios/client";
import { Select } from "../../../fields/base/Select";
import { TextField } from "../../../fields/base/TextField";
import { FaPencil, FaPlus, FaTrash } from "react-icons/fa6";
import { useFieldArray, useForm, Controller, type SubmitHandler } from "react-hook-form";
import type { TFunction } from "i18next";
import { toTitleCase } from "../../../../utils/formatText";
import { isValidAsin, isValidKeywordCharset } from "../../../../modules/advertising/utils/keywordValidation";
import { useAppSelector } from "../../../../redux/hooks";
import { ToggleButton, ToggleButtonGroup, TooltipTrigger } from "react-aria-components";
import { useAddTargetsToAd } from "../../../../modules/advertising/api/useAddTargets";
import { type AddTargetsToAdUpdate } from "../../../../zodios/apiDefinitions/api/ads";
import { TextAreaField } from "../../../fields/base/TextAreaField";
import { Tooltip } from "../../../core/basic/Tooltip";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { flex, vstack } from "../../../../../styled-system/patterns";
import AdvertisementSelector from "../../../../modules/advertising/components/AdvertisementSelector";
import { AD_TYPES, GOALS, WALMART_NEGATIVE_KEYWORD_AD_TYPES } from "../../../core/blocks/AdPlan/constants";

type FormValues = z.infer<ReturnType<typeof getFormSchema>>;
type Target = FormValues["targets"][number];

export type InitialFormValues = Partial<FormValues> & Pick<FormValues, "targets">;

type Props = {
  adId?: number;
  adType?: number;
  isNTMAd?: boolean;
  targetType: string;
  initialData?: InitialFormValues;
  buttonText?: string;
  renderAdSelector?: boolean;
  isDisabled?: boolean;
  onReset?: () => void;
  descriptionText?: string;
  hideNegativeMatchTypeOptions?: boolean;
  negativeOnly?: boolean;
};

type MatchType = keyof typeof MATCH_TYPE_DISPLAY_MAP;

type MatchTypeOptionProps = {
  matchType: MatchType;
  isSelected?: boolean;
  onChange?: (isSelected: boolean) => void;
};
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const getTargetTextSchema = (targetType: string, t: TFunction<any, any>) =>
  z
    .string()
    .trim()
    .nonempty(t(($) => $.advertising["Target cannot be empty."]))
    .refine((kw) => (targetType === "target" ? isValidAsin(kw) : kw.length <= 80), {
      message:
        targetType === "target" ?
          t(($) => $.advertising["Invalid ASIN provided."])
        : t(($) => $.advertising["Keyword must be 80 characters or less."]),
    })
    .refine((kw) => targetType === "target" || (!kw.startsWith(".") && !kw.endsWith(".")), {
      message: t(($) => $.advertising["Keyword can't start or end with a period."]),
    })
    .refine((kw) => targetType === "target" || (!kw.startsWith("-") && !kw.endsWith("-")), {
      message: t(($) => $.advertising["Keyword can't start or end with a hyphen."]),
    })
    .refine((kw) => targetType === "target" || !kw.endsWith("+"), {
      message: t(($) => $.advertising["Keyword can't end with a plus sign."]),
    })
    .refine((kw) => targetType === "target" || isValidKeywordCharset(kw), {
      message: t(($) => $.advertising["Keyword contains invalid characters."]),
    });

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const getFormSchema = (targetType: string, adId: number | undefined, t: TFunction<any, any>) =>
  z
    .object({
      adIds: z.array(z.number()),
      adGroup: z.string(),
      targets: z
        .array(
          z.object({
            text: getTargetTextSchema(targetType, t),
            matchTypes: z.array(z.string()),
          }),
        )
        .min(
          1,
          t(($) => $.advertising["At least one target is required."]),
        ),
    })
    .refine(
      (data) => {
        if (targetType === "target") {
          return data.adIds.length === 1 || adId;
        }
        return true;
      },
      {
        message: t(($) => $.advertising["At least one ad must be selected."]),
        path: ["adIds"],
      },
    );

const DEFAULT_FORM_DATA = {
  adGroup: "",
  targets: [{ text: "", matchTypes: [] }],
  adIds: [],
};

const MATCH_TYPE_DISPLAY_MAP = {
  exact: "E",
  broad: "B",
  phrase: "P",
  negative_exact: "NE",
  negative_phrase: "NP",
  negative_product: "N",
} as const;

const MATCH_TYPE_POLARITY = {
  exact: "positive",
  phrase: "positive",
  broad: "positive",
  negative_exact: "negative",
  negative_phrase: "negative",
  negative_product: "negative",
} satisfies Record<MatchType, "positive" | "negative">;

const OPPOSITE_MATCH_TYPE = {
  exact: "negative_exact",
  phrase: "negative_phrase",
  broad: undefined,
  negative_exact: "exact",
  negative_phrase: "phrase",
  negative_product: undefined,
} satisfies Record<MatchType, MatchType | undefined>;

const isMatchType = (value: string): value is MatchType => value in MATCH_TYPE_DISPLAY_MAP;

// Form state holds match types as plain strings, so this takes one too.
const isNegativeMatchType = (matchType: string) =>
  isMatchType(matchType) && MATCH_TYPE_POLARITY[matchType] === "negative";

const MatchTypeOption = ({ matchType, isSelected, onChange }: MatchTypeOptionProps) => {
  const { t } = useTranslation(["advertising"]);
  const matchTypeLabels: Record<MatchType, string> = {
    exact: t(($) => $.advertising["Exact"]),
    broad: t(($) => $.advertising["Broad"]),
    phrase: t(($) => $.advertising["Phrase"]),
    negative_exact: t(($) => $.advertising["Negative exact"]),
    negative_phrase: t(($) => $.advertising["Negative phrase"]),
    negative_product: t(($) => $.advertising["Negative product"]),
  };
  const label = matchTypeLabels[matchType];
  return (
    <TooltipTrigger>
      <Tooltip>{label}</Tooltip>
      <ToggleButton
        className={({ isSelected }) =>
          css({
            cursor: "pointer",
            border: "none",
            borderRadius: "standard",
            padding: "xsmall",
            color: isSelected ? "trellisPalette.2" : "#999",
            fontSize: "xlarge",
            fontWeight: isSelected ? "extrabold" : "bold",
            backgroundColor: "white",
          })
        }
        aria-label={label}
        isSelected={isSelected}
        onChange={onChange}
      >
        {MATCH_TYPE_DISPLAY_MAP[matchType]}
      </ToggleButton>
    </TooltipTrigger>
  );
};

interface MatchTypesFieldProps {
  options: MatchType[];
  selectedKeys: string[];
  onSelectionChange: (selectedKeys: string[], lastToggledKey: MatchType, lastIsSelected: boolean) => void;
}

function MatchTypesField({ options, selectedKeys, onSelectionChange }: MatchTypesFieldProps) {
  return (
    <ToggleButtonGroup
      selectedKeys={selectedKeys}
      className={flex({
        gap: "medium",
        justifyContent: "end",
      })}
    >
      {options.map((option) => (
        <MatchTypeOption
          key={option}
          matchType={option}
          isSelected={selectedKeys.includes(option)}
          onChange={(isSelected) => {
            const oppositeMatchType = OPPOSITE_MATCH_TYPE[option];

            const nextSelection =
              selectedKeys.includes(option) ?
                selectedKeys.filter((selectedMatchType) => selectedMatchType !== option)
              : [...selectedKeys.filter((prevMatchType) => prevMatchType !== oppositeMatchType), option];

            onSelectionChange(nextSelection, option, isSelected);
          }}
        />
      ))}
    </ToggleButtonGroup>
  );
}

function getAvailableMatchTypeOptions({
  targetType,
  currentChannel,
  hideNegativeMatchTypeOptions,
  negativeOnly,
  allowWalmartNegativeKeywords,
  isAutoDestination,
}: {
  targetType: string;
  currentChannel: string;
  hideNegativeMatchTypeOptions: boolean;
  negativeOnly: boolean;
  allowWalmartNegativeKeywords: boolean;
  isAutoDestination: boolean;
}): MatchType[] {
  const matchTypes: MatchType[] =
    targetType === "keyword" ? ["exact", "phrase", "broad", "negative_exact", "negative_phrase"]
    : targetType === "target" ? ["negative_product"]
    : [];

  // Walmart takes negative keywords on some ad types, but has no negative product targets.
  const channelSupportsNegatives =
    currentChannel === "amazon" || (targetType === "keyword" && allowWalmartNegativeKeywords);

  // `create_targets` refuses positive manual targets on an auto-targeting plan.
  const allowPositives = !negativeOnly && !isAutoDestination;
  const allowNegatives = negativeOnly || (channelSupportsNegatives && !hideNegativeMatchTypeOptions);

  return matchTypes.filter((matchType) => (isNegativeMatchType(matchType) ? allowNegatives : allowPositives));
}

export const AddTargetsModal = ({
  adId,
  adType,
  targetType,
  isNTMAd,
  initialData,
  renderAdSelector = false,
  onReset,
  descriptionText,
  hideNegativeMatchTypeOptions = false,
  negativeOnly = false,
  onOpenChange,
  isOpen,
}: Props & { isOpen: boolean; onOpenChange: (isOpen: boolean) => void }) => {
  const { t } = useTranslation(["advertising", "common"]);
  const [isBulkInputMode, setIsBulkInputMode] = useState(false);
  const [bulkInputTargets, setBulkInputTargets] = useState("");
  const [selectedAdGoal, setSelectedAdGoal] = useState<number | undefined>();
  const [selectedAdType, setSelectedAdType] = useState<number | undefined>();

  const channel = useAppSelector((state) => state.channel);

  const destinationAdType = adType ?? selectedAdType;
  const isAutoDestination = destinationAdType === AD_TYPES.SPONSORED_PRODUCT_AUTO.value;
  const allowWalmartNegativeKeywords =
    channel.currentChannel === "walmart" &&
    destinationAdType != null &&
    WALMART_NEGATIVE_KEYWORD_AD_TYPES.includes(destinationAdType);

  const addTargetsMutation = useAddTargetsToAd();

  const schema = getFormSchema(targetType, adId, t);
  const initialFormData = { ...DEFAULT_FORM_DATA, ...initialData };

  const { control, setValue, reset, handleSubmit, formState, watch } = useForm<FormValues>({
    mode: "onBlur",
    defaultValues: initialFormData,
    resolver: zodResolver(schema),
  });

  const {
    fields: targets,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "targets",
  });

  const watchedTargets = watch("targets");
  const selectedAdIds = watch("adIds");
  const selectedAdGroup = watch("adGroup");

  const isSelectedAdNTM = selectedAdIds[0] != null && selectedAdGoal === GOALS.IMPORTED_ADS.value;

  const isSubmitDisabled =
    !formState.isValid ||
    ((isNTMAd || isSelectedAdNTM) && !selectedAdGroup) ||
    (!adId && selectedAdIds.length === 0) ||
    (targetType === "keyword" && watchedTargets.some((target) => !target.matchTypes.length)) ||
    // An ASIN with no match type posts as a positive product target, which an auto plan refuses.
    (isAutoDestination &&
      targetType === "target" &&
      watchedTargets.some((target) => !target.matchTypes.includes("negative_product")));

  const adGroupOptionsQuery = useQuery({
    queryKey: ["adGroups", adId, selectedAdIds],
    queryFn: async () => {
      const ad = adId ?? selectedAdIds[0];
      if (typeof ad !== "number") {
        throw new Error("Valid advertisement ID is required");
      }
      return await apiClient.getAdGroups({ queries: { advertisement: ad } });
    },
    enabled: (!!adId && !!isNTMAd) || selectedAdIds.length > 0,
    select: (adGroupsRes) => {
      return adGroupsRes.map((adGroup) => ({
        label: adGroup.ad_group_name,
        value: adGroup.ad_group_id,
      }));
    },
  });

  const onSubmit: SubmitHandler<FormValues> = ({ adGroup, targets }) => {
    const destinationAdId = adId ?? selectedAdIds[0];
    if (typeof destinationAdId !== "number") {
      return;
    }

    const body: AddTargetsToAdUpdate = {
      data: {
        advertisement: adId ?? selectedAdIds[0]!,
        target_type: targetType,
        channel: channel.currentChannel,
        action: "create_targets",
      },
    };

    if (isNTMAd || isSelectedAdNTM) {
      body.data.ad_group = adGroup.trim();
    }

    if (targetType === "keyword") {
      body.data.targets = targets;
    }

    if (targetType === "target") {
      body.data.target_type = "product";

      const productTargets = targets;

      body.data.targets = {
        products: productTargets.map((t) => t.text),
        targetTypes: productTargets.map((t) =>
          t.matchTypes.includes("negative_product") ? "negative_product" : "product",
        ),
      };
    }
    addTargetsMutation.mutate(body);
    if (!renderAdSelector) {
      onOpenChange(false);
    }
    reset(initialFormData);
    onReset?.();
  };

  const toggleAllMatchType = (matchType: MatchType, on: boolean) => {
    const oppositeMatchType = OPPOSITE_MATCH_TYPE[matchType];

    const updatedTargets = watchedTargets.map((target) => {
      const currentTypes = target.matchTypes;
      const hasMatchType = currentTypes.includes(matchType);

      let newMatchTypes = [...currentTypes].filter((mt) => mt !== oppositeMatchType);

      if (on && !hasMatchType) {
        newMatchTypes = [...newMatchTypes, matchType];
      } else if (!on && hasMatchType) {
        newMatchTypes = newMatchTypes.filter((mt) => mt !== matchType);
      }

      return { ...target, matchTypes: newMatchTypes };
    });

    updatedTargets.forEach((t, idx) => {
      setValue(`targets.${idx}.matchTypes`, t.matchTypes, { shouldValidate: true, shouldDirty: true });
    });
  };

  const matchTypeOptions = getAvailableMatchTypeOptions({
    targetType,
    currentChannel: channel.currentChannel,
    hideNegativeMatchTypeOptions,
    negativeOnly,
    allowWalmartNegativeKeywords,
    isAutoDestination,
  });

  const matchTypesSelectedForAll = matchTypeOptions.filter((matchType) =>
    watchedTargets.every((target) => target.matchTypes.includes(matchType)),
  );

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          reset(initialFormData);
        }
        onOpenChange(isOpen);
      }}
      title={
        targetType === "target" ? t(($) => $.advertising["Add New ASINs"])
        : targetType === "keyword" ?
          t(($) => $.advertising["Add New Keywords"])
        : t(($) => $.advertising["Add New {targetType}s"], { targetType: toTitleCase(targetType) })
      }
      css={{
        width: renderAdSelector ? "1200px" : "800px",
        height: renderAdSelector ? "1100px" : "inherit",
        overflowY: "auto",
      }}
    >
      {isBulkInputMode ?
        <div className={css({ padding: "large" })}>
          <TextAreaField
            value={bulkInputTargets}
            onChange={(value) => {
              setBulkInputTargets(value);
            }}
            rows={8}
            className={css({ width: "100%", fontSize: "medium" })}
            aria-label={
              targetType === "target" ? t(($) => $.advertising["Enter ASINs"])
              : targetType === "keyword" ?
                t(($) => $.advertising["Enter keywords"])
              : t(($) => $.advertising["Enter {targetType}s"], { targetType })
            }
          />
          <p className={css({ margin: 0, fontSize: "medium" })}>
            {targetType === "target" ?
              t(($) => $.advertising["Separate ASINs using commas or new lines"])
            : targetType === "keyword" ?
              t(($) => $.advertising["Separate keywords using commas or new lines"])
            : t(($) => $.advertising["Separate {targetType}s using commas or new lines"], { targetType })}
          </p>
          <Flex gap={"large"} justifyContent={"center"}>
            <Button variant="secondary" onPress={() => setIsBulkInputMode(false)}>
              {t(($) => $.common.actions.Cancel)}
            </Button>
            <Button
              onPress={() => {
                const textSchema = getTargetTextSchema(targetType, t);
                const targetsToAdd = [
                  ...new Set(
                    bulkInputTargets
                      .split(/[\n,]/)
                      .map((t) => t.trim())
                      .filter((t) => !!t),
                  ),
                ];

                const currentTargets = watchedTargets.map((t) => t.text);
                const updatedTargets: Target[] = [];

                targetsToAdd.forEach((targetText) => {
                  const result = textSchema.safeParse(targetText);
                  if (!result.success) {
                    return;
                  }
                  // Keep match types for existing targets, otherwise push new targets with default match type selection
                  if (currentTargets.includes(targetText)) {
                    const existingTarget = watchedTargets.find((t) => t.text === targetText);
                    if (existingTarget) {
                      updatedTargets.push(existingTarget);
                    }
                  } else {
                    updatedTargets.push({ text: targetText, matchTypes: [] });
                  }
                });
                if (updatedTargets.length) {
                  setValue("targets", updatedTargets, { shouldValidate: true, shouldDirty: true });
                } else {
                  reset(DEFAULT_FORM_DATA);
                }
                setIsBulkInputMode(false);
              }}
            >
              {targetType === "target" ?
                t(($) => $.advertising["Update Targets"])
              : targetType === "keyword" ?
                t(($) => $.advertising["Update Keywords"])
              : t(($) => $.advertising["Update {targetType}s"], { targetType: toTitleCase(targetType) })}
            </Button>
          </Flex>
        </div>
      : <form
          onSubmit={handleSubmit(onSubmit)}
          className={vstack({ gap: "medium", alignItems: "stretch", height: "100%", paddingX: "xlarge" })}
        >
          <p className={css({ margin: 0, fontSize: "medium", textAlign: "center" })}>
            {descriptionText ?
              descriptionText
            : targetType === "target" ?
              adId ?
                t(($) => $.advertising["Add ASIN targets to this media plan by entering them below."])
              : t(($) => $.advertising["Add ASIN targets to a media plan by entering them below."])
            : adId ?
              t(
                ($) =>
                  $.advertising[
                    "Add keywords to this media plan by entering them below and selecting your preferred match types. At least one match type is required for each keyword."
                  ],
              )
            : t(
                ($) =>
                  $.advertising[
                    "Add keywords to a media plan by entering them below and selecting your preferred match types. At least one match type is required for each keyword."
                  ],
              )
            }
          </p>
          {isAutoDestination && (
            <p className={css({ margin: 0, fontSize: "medium", textAlign: "center", color: "text.label" })}>
              {t(
                ($) =>
                  $.advertising["Auto-targeting media plans choose their own targets, so only negations can be added."],
              )}
            </p>
          )}
          <div
            className={css({
              display: "grid",
              position: "relative",
              gap: "large",
              gridTemplateColumns: renderAdSelector ? "1fr 1fr" : "1fr",
              _before:
                renderAdSelector ?
                  {
                    content: '""',
                    position: "absolute",
                    top: "145px",
                    bottom: 0,
                    left: "50%",
                    width: "1px",
                    backgroundColor: "gray.200",
                    transform: "translateX(-50%)",
                  }
                : undefined,
            })}
          >
            {renderAdSelector && (
              <div className={css({ overflow: "auto" })}>
                <label className={css({ color: "text.label", fontSize: "medium", fontWeight: "bold" })}>
                  {targetType === "target" ?
                    t(($) => $.advertising["Select a Media Plan to Add Targets To"])
                  : targetType === "keyword" ?
                    t(($) => $.advertising["Select a Media Plan to Add Keywords To"])
                  : t(($) => $.advertising["Select a Media Plan to Add {targetType}s To"], {
                      targetType: toTitleCase(targetType),
                    })
                  }
                </label>
                <Controller
                  name="adIds"
                  control={control}
                  render={({ field }) => (
                    <AdvertisementSelector
                      selectedRowIds={selectedAdIds}
                      onSelectionChange={(updatedAdIds, selectedRows) => {
                        field.onChange(updatedAdIds.map((id) => parseInt(id.toString())));
                        setSelectedAdGoal(selectedRows[0]?.ad_goal);
                        setSelectedAdType(selectedRows[0]?.ad_type);
                        setValue("adGroup", "", { shouldDirty: true, shouldValidate: true });
                        if (selectedRows[0]?.ad_type === AD_TYPES.SPONSORED_PRODUCT_AUTO.value) {
                          // Positives picked for another plan, or prefilled by the caller, would
                          // post invisibly once the toggles for them are gone.
                          watchedTargets.forEach((target, index) =>
                            setValue(`targets.${index}.matchTypes`, target.matchTypes.filter(isNegativeMatchType), {
                              shouldDirty: true,
                              shouldValidate: true,
                            }),
                          );
                        }
                      }}
                      defaultSelectedColumns={[]}
                      enableMultiRowSelection={false}
                      canSetPageSize={false}
                      context={"addTargetsWidget"}
                    />
                  )}
                />
              </div>
            )}
            <div className={css({ marginTop: renderAdSelector ? "111px" : 0 })}>
              {(isNTMAd || isSelectedAdNTM) && (
                <Controller
                  name="adGroup"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label={t(($) => $.advertising["Ad Group"])}
                      onSelectionChange={(value) => {
                        field.onChange(value);
                      }}
                      selectedKey={field.value}
                      items={adGroupOptionsQuery.data}
                      css={{ marginLeft: "xlarge" }}
                    />
                  )}
                />
              )}
              <Flex gap="large" alignItems={"center"} paddingRight="medium" scrollbarGutter={"stable"} overflowY="auto">
                <Flex
                  className={css({
                    gap: "small",
                    justifyContent: "center",
                    alignItems: "center",
                    marginLeft: renderAdSelector ? "xlarge" : "inherit",
                  })}
                >
                  <label className={css({ color: "text.label", fontSize: "medium", fontWeight: "bold" })}>
                    {targetType === "keyword" ?
                      t(($) => $.advertising["Keywords"])
                    : t(($) => $.advertising["Targets"])}
                  </label>
                  <TooltipTrigger>
                    <Tooltip placement={"top"}>
                      <span>
                        {targetType === "target" ?
                          t(($) => $.advertising["Bulk input a list of ASINs from your clipboard"])
                        : targetType === "keyword" ?
                          t(($) => $.advertising["Bulk input a list of keywords from your clipboard"])
                        : t(($) => $.advertising["Bulk input a list of {targetType}s from your clipboard"], {
                            targetType,
                          })
                        }
                      </span>
                    </Tooltip>
                    <Button
                      variant={"contentOnly"}
                      onPress={() => {
                        setBulkInputTargets(watchedTargets.map((t) => t.text).join("\n"));
                        setIsBulkInputMode(true);
                      }}
                    >
                      <FaPencil />
                    </Button>
                  </TooltipTrigger>
                </Flex>
                <div className={css({ flex: 1 })}></div>
                {matchTypeOptions.length ?
                  <MatchTypesField
                    options={matchTypeOptions}
                    selectedKeys={matchTypesSelectedForAll}
                    onSelectionChange={(_, lastToggledKey, lastIsSelected) => {
                      toggleAllMatchType(lastToggledKey, lastIsSelected);
                    }}
                  />
                : null}
                <div className={css({ minWidth: "18px" })} />
              </Flex>
              <Flex
                className={css({
                  gap: "small",
                  flexDirection: "column",
                  borderTop: "1px solid token(colors.gray.200)",
                  borderBottom: "1px solid token(colors.gray.200)",
                  maxHeight: "350px",
                  paddingRight: "medium",
                  paddingTop: "small",
                  paddingBottom: "small",
                  scrollbarGutter: "stable",
                  overflowY: "auto",
                })}
              >
                {targets.map((target, index) => {
                  return (
                    <Flex
                      key={target.id}
                      className={css({
                        position: "relative",
                        gap: "large",
                        alignItems: "center",
                      })}
                    >
                      {index === targets.length - 1 ?
                        <Button
                          variant={"contentOnly"}
                          className={css({
                            color: "gray",
                            fontSize: "xlarge",
                            fontWeight: "bold",
                          })}
                          onPress={() => {
                            append({ text: "", matchTypes: [] });
                          }}
                        >
                          <FaPlus />
                        </Button>
                      : <div className={css({ minWidth: "18px" })} />}
                      <Controller
                        control={control}
                        name={`targets.${index}.text`}
                        render={({ field, fieldState }) => {
                          return (
                            <TextField
                              {...field}
                              errorMessage={fieldState.error?.message}
                              placeholder={
                                targetType === "target" ?
                                  t(($) => $.advertising["Enter target ASINs..."])
                                : t(($) => $.advertising["Enter target text..."])
                              }
                              aria-label={
                                targetType === "target" ?
                                  t(($) => $.advertising["Target ASINs"])
                                : t(($) => $.advertising["Target text"])
                              }
                            />
                          );
                        }}
                      />
                      {matchTypeOptions.length ?
                        <Controller
                          control={control}
                          name={`targets.${index}.matchTypes`}
                          render={({ field }) => (
                            <MatchTypesField
                              options={matchTypeOptions}
                              selectedKeys={field.value}
                              onSelectionChange={(value) => {
                                field.onChange(value);
                                field.onBlur();
                              }}
                            />
                          )}
                        />
                      : null}

                      {targets.length > 1 ?
                        <Button
                          variant={"contentOnly"}
                          className={css({
                            color: "gray",
                            fontSize: "xlarge",
                          })}
                          onPress={() => remove(index)}
                        >
                          <FaTrash />
                        </Button>
                      : <div className={css({ minWidth: "18px" })} />}
                    </Flex>
                  );
                })}
              </Flex>
              {renderAdSelector && (
                <Flex justifyContent={"center"} padding={"large"}>
                  <Button type={"submit"} isDisabled={isSubmitDisabled}>
                    {targetType === "target" ?
                      t(($) => $.advertising["Add Targets"], { count: targets.length })
                    : targetType === "keyword" ?
                      t(($) => $.advertising["Add Keywords"], { count: targets.length })
                    : t(($) => $.advertising["Add {targetType}s"], {
                        count: targets.length,
                        targetType: toTitleCase(targetType),
                      })
                    }
                  </Button>
                </Flex>
              )}
            </div>
          </div>
          <Flex className={css({ gap: "large", justifyContent: "center", marginTop: "auto" })}>
            <Button
              variant={"secondary"}
              onPress={() => {
                onOpenChange(false);
              }}
            >
              {t(($) => $.common.actions.Cancel)}
            </Button>
            {renderAdSelector ?
              <Button
                onPress={() => {
                  onOpenChange(false);
                }}
              >
                {t(($) => $.advertising["Finished"])}
              </Button>
            : <Button type={"submit"} isDisabled={isSubmitDisabled}>
                {targetType === "target" ?
                  t(($) => $.advertising["Add Targets"], { count: targets.length })
                : targetType === "keyword" ?
                  t(($) => $.advertising["Add Keywords"], { count: targets.length })
                : t(($) => $.advertising["Add {targetType}s"], {
                    count: targets.length,
                    targetType: toTitleCase(targetType),
                  })
                }
              </Button>
            }
          </Flex>
        </form>
      }
    </Modal>
  );
};

const AddTargetsWidget = (props: Props) => {
  const { t } = useTranslation(["advertising"]);
  const [open, setOpen] = useState(false);

  return (
    <>
      <AddTargetsModal {...props} isOpen={open} onOpenChange={setOpen} initialData={props.initialData} />

      <Button isDisabled={props.isDisabled} onPress={() => setOpen(true)}>
        {props.buttonText ??
          (props.targetType === "target" ? t(($) => $.advertising["Add New ASINs"])
          : props.targetType === "keyword" ? t(($) => $.advertising["Add New Keywords"])
          : t(($) => $.advertising["Add New {targetType}s"], { targetType: toTitleCase(props.targetType) }))}
      </Button>
    </>
  );
};

export default AddTargetsWidget;
